import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { BookOpenIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const CONTENT_BASE = '/content';

async function fetchJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to load (${res.status})`);
  return res.json();
}

const HelpPage = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();

  const [index, setIndex]       = useState(null);
  const [article, setArticle]   = useState(null);
  const [indexError, setIndexError]   = useState(null);
  const [articleError, setArticleError] = useState(null);
  const [loadingIndex, setLoadingIndex]   = useState(true);
  const [loadingArticle, setLoadingArticle] = useState(false);

  // Load the index once on mount
  useEffect(() => {
    setLoadingIndex(true);
    fetchJSON(`${CONTENT_BASE}/help/index.json`)
      .then(data => {
        setIndex(data);
        // If no article selected, default to the first one
        if (!articleId && data.articles?.length > 0) {
          navigate(`/help/${data.articles[0].id}`, { replace: true });
        }
      })
      .catch(err => setIndexError(err.message))
      .finally(() => setLoadingIndex(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the article whenever articleId changes
  useEffect(() => {
    if (!articleId) return;
    setLoadingArticle(true);
    setArticleError(null);
    setArticle(null);
    fetchJSON(`${CONTENT_BASE}/help/articles/${articleId}.json`)
      .then(setArticle)
      .catch(err => setArticleError(err.message))
      .finally(() => setLoadingArticle(false));
  }, [articleId]);

  if (loadingIndex) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (indexError) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center">
        <BookOpenIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">Help content unavailable</h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm">{indexError}</p>
      </div>
    );
  }

  const { categories = [], articles = [] } = index || {};

  // Group articles by category
  const byCategory = categories.map(cat => ({
    ...cat,
    articles: articles.filter(a => a.category === cat.id),
  }));

  return (
    <div className="flex gap-8 min-h-[60vh]">

      {/* Sidebar nav */}
      <aside className="hidden md:block w-56 flex-shrink-0">
        <nav className="sticky top-8 space-y-6">
          {byCategory.map(cat => (
            cat.articles.length === 0 ? null : (
              <div key={cat.id}>
                <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
                  {cat.title}
                </p>
                <ul className="space-y-1">
                  {cat.articles.map(a => (
                    <li key={a.id}>
                      <Link
                        to={`/help/${a.id}`}
                        className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-sm transition-colors ${
                          a.id === articleId
                            ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-medium'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {a.id === articleId && (
                          <ChevronRightIcon className="h-3 w-3 flex-shrink-0" />
                        )}
                        <span className={a.id === articleId ? '' : 'ml-[18px]'}>{a.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )
          ))}
        </nav>
      </aside>

      {/* Article area */}
      <div className="flex-1 min-w-0">

        {/* Mobile: category + article picker */}
        <div className="md:hidden mb-6">
          <select
            value={articleId || ''}
            onChange={e => navigate(`/help/${e.target.value}`)}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {byCategory.map(cat =>
              cat.articles.length === 0 ? null : (
                <optgroup key={cat.id} label={cat.title}>
                  {cat.articles.map(a => (
                    <option key={a.id} value={a.id}>{a.title}</option>
                  ))}
                </optgroup>
              )
            )}
          </select>
        </div>

        {/* Article content */}
        {loadingArticle && (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        )}

        {articleError && !loadingArticle && (
          <div className="py-12 text-center">
            <p className="text-gray-500 dark:text-gray-400 text-sm">{articleError}</p>
          </div>
        )}

        {article && !loadingArticle && (
          <article>
            <div className="mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{article.title}</h1>
              {article.lastUpdated && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Updated {article.lastUpdated}</p>
              )}
            </div>
            <div className="prose prose-sm prose-gray dark:prose-invert max-w-none
              prose-headings:font-semibold prose-headings:text-gray-800 dark:prose-headings:text-gray-200
              prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-3
              prose-h3:text-base prose-h3:mt-6 prose-h3:mb-2
              prose-p:text-gray-600 dark:prose-p:text-gray-400 prose-p:leading-relaxed
              prose-li:text-gray-600 dark:prose-li:text-gray-400
              prose-strong:text-gray-800 dark:prose-strong:text-gray-200
              prose-a:text-primary-600 dark:prose-a:text-primary-400 prose-a:no-underline hover:prose-a:underline
              prose-code:bg-gray-100 dark:prose-code:bg-gray-700 prose-code:px-1 prose-code:rounded prose-code:text-sm
              prose-ol:list-decimal prose-ul:list-disc">
              <ReactMarkdown>{article.content}</ReactMarkdown>
            </div>
          </article>
        )}

        {!articleId && !loadingIndex && (
          <div className="py-16 text-center text-gray-400 dark:text-gray-500">
            <BookOpenIcon className="h-10 w-10 mx-auto mb-3" />
            <p>Select an article to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpPage;
